/**
 * Core scope-catalog client.
 *
 * Reads the granular OAuth/MCP scope catalog from Core's public endpoint
 * (`GET /authorization/v2/oidc/scopes/`) so the agent scope picker renders real,
 * current scopes instead of a hardcoded list. The catalog is non-sensitive
 * vocabulary (no auth), and effectively static, so the React Query hook caches
 * it aggressively.
 */

import { useQuery } from '@tanstack/react-query'

import type { ScopeRow } from 'src/features/workspaces/components/agents/scopeModel'

type RawScopeRow = {
  code: string
  description: string
  resource: string
  action: string
  app: string
  enforced_by_core: boolean
  deprecated: boolean
}

const coreApiBase = (): string =>
  (import.meta.env.VITE_APP_CORE_API as string | undefined)?.replace(
    /\/$/,
    ''
  ) ?? ''

export const fetchScopeCatalog = async (): Promise<ScopeRow[]> => {
  const response = await fetch(
    `${coreApiBase()}/authorization/v2/oidc/scopes/`,
    {
      headers: { Accept: 'application/json' }
    }
  )
  if (!response.ok) {
    throw new Error(`scope catalog request failed (${response.status})`)
  }
  const data = (await response.json()) as { scopes: RawScopeRow[] }

  return (data.scopes ?? []).map((r) => ({
    code: r.code,
    description: r.description,
    resource: r.resource,
    action: r.action,
    app: r.app,
    enforcedByCore: r.enforced_by_core,
    deprecated: r.deprecated
  }))
}

export const SCOPE_CATALOG_QUERY_KEY = ['core', 'scopeCatalog'] as const

export const useScopeCatalog = () =>
  useQuery({
    queryKey: SCOPE_CATALOG_QUERY_KEY,
    queryFn: fetchScopeCatalog,
    staleTime: 1000 * 60 * 60, // 1h — the catalog is effectively static
    gcTime: 1000 * 60 * 60
  })
